//? Libs
import DOMPurify from "dompurify";
import { parseHtmlToText } from "../../utils/questions/questionParseText";

//* Importing modules
import React, { useState, useEffect, useCallback } from "react";

//* Importing components from react-bootstrap
import { Modal, Form, InputGroup } from "react-bootstrap";

//* Importing icons
import { RiCloseFill } from "react-icons/ri";

//! Custom components
import DefaultButton from "../../component/base/DefaultButton";
import { TagsSelector } from "../../component/common/TagsSelector";
import { TableDrag } from "../ExamView/TableDrag";
import ModalManage from "../QuestionsView/list/components/ModalManage";

//! Custom hooks
import { useQuestions } from "../../hooks/questions/useQuestions";

//? Importing types
import {
  QuestionDetails,
  QuestionType,
} from "../../hooks/questions/types/types";

type ExamModalTagProps = {
  showModalTag: boolean;
  onClose: () => void;
  disciplineId: string;
  disciplineName: string;
  token: string;
  examType: QuestionType; // Usamos examType para indicar o tipo da prova
  onSelectQuestions: (selectedQuestions: QuestionDetails[]) => void;
};

/**
 * Componente que exibe um modal para buscar e selecionar questões da prova por tags.
 *
 * @param {boolean} showModalTag - Flag para mostrar ou ocultar o modal.
 * @param {() => void} onClose - Função para fechar o modal.
 * @param {string} disciplineId - ID da disciplina para buscar as questões.
 * @param {string} disciplineName - Nome da disciplina.
 * @param {string} token - Token de autenticação para requisições.
 * @param {QuestionType} examType - Tipo de prova (objective ou dissertative).
 * @param {(questions: QuestionDetails[]) => void} onSelectQuestions - Função para tratar as questões selecionadas.
 *
 * @returns {JSX.Element} Componente renderizado.
 */
export function ExamModalTag({
  showModalTag,
  onClose,
  disciplineId,
  disciplineName,
  token,
  examType,
  onSelectQuestions,
}: ExamModalTagProps) {
  const { fetchQuestionsByDiscipline } = useQuestions(token);

  // State para as questões buscadas por tags
  const [questionsTag, setQuestionsTag] = useState<QuestionDetails[]>([]);
  // State para o array formatado para exibição na tabela
  const [questionsArrayTag, setQuestionsArrayTag] = useState<any[]>([]);
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [loading, setLoading] = useState<boolean>(false);

  const [selectedQuestionId, setSelectedQuestionId] = useState<string | null>(
    null
  );
  const [showModalManage, setShowModalManage] = useState<boolean>(false);

  // Função para buscar questões pela disciplina, examType e tags selecionadas
  const fetchQuestions = useCallback(async () => {
    if (!disciplineId || selectedTags.length === 0) {
      setQuestionsTag([]);
      return;
    }
    setLoading(true);
    try {
      const res = await fetchQuestionsByDiscipline(
        disciplineId,
        examType,
        1,
        1000,
        undefined,
        selectedTags
      );
      if (res && res.questions) {
        setQuestionsTag(res.questions);
      } else {
        setQuestionsTag([]);
      }
    } catch (error) {
      console.error("Erro ao buscar questões por tags:", error);
      setQuestionsTag([]);
    } finally {
      setLoading(false);
    }
  }, [disciplineId, examType, selectedTags, fetchQuestionsByDiscipline]);

  useEffect(() => {
    fetchQuestions();
  }, [fetchQuestions]);

  // Quando o examType mudar, filtra as questões já buscadas
  useEffect(() => {
    const validTypes =
      examType === "objective" ? ["objective"] : ["dissertative"];
    setQuestionsTag((prev) =>
      prev.filter((q) =>
        q.subQuestions?.some((subQ) => validTypes.includes(subQ.type))
      )
    );
  }, [examType]);

  // Converte as questões para o formato esperado pelo TableDrag
  useEffect(() => {
    const questoes = questionsTag.map((item, i) => {
      let tipoQuestaoText = "";
      let enunciadoContent: React.ReactElement = (
        <p className="text_overflow">
          {String(
            parseHtmlToText(String(item.statement ?? ""), DOMPurify.sanitize)
          )}
        </p>
      );

      if (item.subQuestions && item.subQuestions.length > 0) {
        const types = item.subQuestions.map((sq) => sq.type);
        const uniqueTypes = Array.from(new Set(types));

        if (uniqueTypes.length === 1) {
          if (uniqueTypes[0] === "objective") {
            tipoQuestaoText = "Objetiva";
            const objectiveSub = item.subQuestions.find(
              (sq) => sq.type === "objective"
            );
            if (objectiveSub && objectiveSub.statement) {
              enunciadoContent = (
                <p className="text_overflow">
                  {String(
                    parseHtmlToText(
                      String(objectiveSub.statement),
                      DOMPurify.sanitize
                    )
                  )}
                </p>
              );
            }
          } else if (uniqueTypes[0] === "dissertative") {
            tipoQuestaoText = "Dissertativa";
            const dissertativeSub = item.subQuestions.find(
              (sq) => sq.type === "dissertative"
            );
            if (dissertativeSub && dissertativeSub.statement) {
              enunciadoContent = (
                <p className="text_overflow">
                  {String(
                    parseHtmlToText(
                      String(dissertativeSub.statement),
                      DOMPurify.sanitize
                    )
                  )}
                </p>
              );
            }
          } else {
            tipoQuestaoText = "Texto em Comun";
            enunciadoContent = (
              <p className="text_overflow">
                {String(
                  parseHtmlToText(String(item.statement), DOMPurify.sanitize)
                )}
              </p>
            );
          }
        } else {
          tipoQuestaoText = "Texto em Comun";
          const dissertativeSub = item.subQuestions.find(
            (sq) => sq.type === "dissertative"
          );
          if (dissertativeSub && dissertativeSub.statement) {
            enunciadoContent = (
              <p className="text_overflow">
                {String(
                  parseHtmlToText(
                    String(dissertativeSub.statement),
                    DOMPurify.sanitize
                  )
                )}
              </p>
            );
          } else {
            enunciadoContent = (
              <p className="text_overflow">
                {String(
                  parseHtmlToText(
                    String(item.subQuestions[0].statement),
                    DOMPurify.sanitize
                  )
                )}
              </p>
            );
          }
        }
      }

      const disciplina = item.discipline?.name || "Sem disciplina";

      return {
        numero: i + 1,
        id: item.id,
        index: i,
        enunciado: enunciadoContent,
        disciplina: disciplina,
        tipoQuestao: <p style={{ whiteSpace: "pre" }}>{tipoQuestaoText}</p>,
        acoes: i,
      };
    });
    setQuestionsArrayTag(questoes);
  }, [questionsTag]);

  const handleIncludeQuestions = () => {
    onSelectQuestions(questionsTag);
    handleClose();
  };

  const handleClose = () => {
    setSelectedTags([]);
    setQuestionsTag([]);
    setQuestionsArrayTag([]);
    onClose();
  };

  return (
    <Modal
      show={showModalTag}
      dialogClassName="modal_width"
      onHide={handleClose}
    >
      <Modal.Header closeButton closeLabel="">
        <Modal.Title>Buscar questões da disciplina por tags</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {disciplineId && (
          <div>
            <InputGroup>
              <div
                className="flex fdcolumn flex1 margin-bottom"
                style={{ position: "relative" }}
              >
                <Form.Label>Selecione uma ou mais tags</Form.Label>
                <TagsSelector
                  hideCreateTagButton
                  selectedTagIds={selectedTags}
                  onChange={(selectedTagIds) => setSelectedTags(selectedTagIds)}
                />
              </div>
            </InputGroup>
            <Form.Label>Questões filtradas por tags selecionadas</Form.Label>
            <br />
            <Form.Label>
              Total:{" "}
              <span style={{ fontWeight: "normal" }}>
                {questionsTag.length}{" "}
                {questionsTag.length === 1 ? "questão" : "questões"}
              </span>
            </Form.Label>
            <InputGroup>
              {loading ? (
                <div
                  style={{
                    width: "100%",
                    display: "flex",
                    justifyContent: "center",
                    padding: "20px 0",
                  }}
                >
                  <div className="spinner-border text-primary" role="status" />
                </div>
              ) : (
                <TableDrag
                  draggable={true}
                  data={questionsArrayTag}
                  showData={(index) => {
                    const questionId = questionsArrayTag[index].id;
                    setSelectedQuestionId(questionId);
                    setShowModalManage(true);
                  }}
                  deleteData={(index: number) => {
                    const questao = questionsArrayTag[index];
                    const updatedQuestions = questionsTag.filter(
                      (q) => q.id !== questao.id
                    );
                    setQuestionsTag(updatedQuestions);
                  }}
                  setData={(updatedData) => {
                    setQuestionsArrayTag(updatedData || []);
                  }}
                />
              )}
            </InputGroup>
          </div>
        )}
      </Modal.Body>
      <Modal.Footer>
        <div className="w100 inline-flex jcsb margin-top-1">
          <DefaultButton
            bg="secondary"
            text="Fechar"
            icon={<RiCloseFill />}
            onClick={(e) => {
              handleClose();
              // return;
            }}
          />
          <DefaultButton
            type="button"
            onClick={(e) => {
              handleIncludeQuestions();
              // return;
            }}
            bg="confirm"
            text="Incluir questões"
            loadingtext="Incluindo questões"
            disabled={questionsTag.length === 0}
          />
        </div>
      </Modal.Footer>
      <ModalManage
        id={selectedQuestionId}
        token={token}
        showModalManage={showModalManage}
        setShowModalManage={() => setShowModalManage(false)}
      />
    </Modal>
  );
}
